import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Slider,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { formatSpeakerLabel, getSpeakerColorByLabel } from "./speakerColors";
import { formatPreciseTimestamp, parseTimestamp } from "../utils/timeFormat";
import { FRAME_STEP_LARGE, FRAME_STEP_SMALL } from "../constants";
import { ArmedState } from "../hooks/useRangeArming";

export interface RangeMarker {
  uid: string;
  start: number;
  end: number;
  speaker: string | number;
  active: boolean;
}

interface TimeRangeSelectorProps {
  duration: number;
  currentTime: number;
  onScrub: (t: number) => void;
  markers: RangeMarker[];
  speakers: string[];
  armed: ArmedState | null;
  /** Human label for what's being edited, e.g. "Editing Speaker A" / "New entry". */
  armedLabel?: string;
  /** Force-show the arming panel (read-only) for the walkthrough. */
  preview?: boolean;
  canCommit: boolean;
  /** No arg → set to current playhead; a value → typed/explicit time (seconds). */
  onSetStart: (value?: number) => void;
  onSetEnd: (value?: number) => void;
  onCommit: () => void;
  onCancel: () => void;
}

/** Seconds of timeline shown in the fine-seek window. */
const FINE_WINDOW = 16;

const pct = (t: number, duration: number) =>
  duration > 0 ? `${Math.min(100, Math.max(0, (t / duration) * 100))}%` : "0%";

const clamp = (t: number, duration: number) =>
  Math.max(0, duration > 0 ? Math.min(t, duration) : t);

/** Editable "M:SS.cc" timestamp field; commits a parsed value on blur/Enter. */
const TimestampField: React.FC<{
  label: string;
  value: number | null;
  duration: number;
  onCommit: (seconds: number) => void;
}> = ({ label, value, duration, onCommit }) => {
  const [text, setText] = useState("");
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setText(value != null ? formatPreciseTimestamp(value) : "");
  }, [value, focused]);

  const commit = () => {
    setFocused(false);
    const parsed = parseTimestamp(text);
    if (parsed != null) onCommit(clamp(parsed, duration));
  };

  const invalid = text.trim() !== "" && parseTimestamp(text) === null;

  return (
    <TextField
      label={label}
      size="small"
      value={text}
      placeholder="m:ss"
      error={invalid}
      onFocus={() => setFocused(true)}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit();
      }}
      sx={{ width: 116, "& input": { fontFamily: "monospace" } }}
    />
  );
};

/**
 * The editor's video timeline: scrub to seek, see every entry's range as a
 * colored span (conflicts in red), and — when an entry/new-entry is "armed" —
 * set its start/end by typing exact timestamps, dropping markers at the
 * playhead, or fine-scrubbing a zoomed window (so long videos stay precise).
 */
export const TimeRangeSelector: React.FC<TimeRangeSelectorProps> = ({
  duration,
  currentTime,
  onScrub,
  markers,
  speakers,
  armed,
  armedLabel,
  preview = false,
  canCommit,
  onSetStart,
  onSetEnd,
  onCommit,
  onCancel,
}) => {
  const theme = useTheme();
  const railRef = useRef<HTMLDivElement>(null);

  // The arming panel shows while a range is being set, or in preview (the
  // walkthrough) where it's read-only. Handlers are inert without a real armed
  // target so the demo can't mutate anything.
  const showPanel = Boolean(armed) || preview;
  const armedStart = armed?.start ?? null;
  const armedEnd = armed?.end ?? null;
  const commitEnabled = Boolean(armed) && canCommit;
  const handleSetStart = (v?: number) => {
    if (armed) onSetStart(v);
  };
  const handleSetEnd = (v?: number) => {
    if (armed) onSetEnd(v);
  };
  const handleCommit = () => {
    if (armed) onCommit();
  };
  const handleCancel = () => {
    if (armed) onCancel();
  };

  // Fine-seek window recenters only when the playhead leaves it, so dragging
  // gives precise control without the thumb constantly jumping.
  const [fineCenter, setFineCenter] = useState(currentTime);
  useEffect(() => {
    const half = FINE_WINDOW / 2;
    if (currentTime < fineCenter - half || currentTime > fineCenter + half) {
      setFineCenter(currentTime);
    }
  }, [currentTime, fineCenter]);

  const seekFromClient = (clientX: number) => {
    const rail = railRef.current;
    if (!rail || duration <= 0) return;
    const rect = rail.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    onScrub(ratio * duration);
  };

  const step = (delta: number) => onScrub(clamp(currentTime + delta, duration));

  const half = FINE_WINDOW / 2;
  const fineMin = Math.max(0, fineCenter - half);
  const fineMax = Math.min(duration || fineCenter + half, fineCenter + half);

  return (
    <Box>
      {/* Marker rail */}
      <Box
        ref={railRef}
        onClick={(e) => seekFromClient(e.clientX)}
        sx={{ position: "relative", height: 26, cursor: "pointer", mt: 0.5 }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: 0,
            right: 0,
            height: 4,
            borderRadius: 2,
            transform: "translateY(-50%)",
            backgroundColor: alpha(theme.palette.text.primary, 0.15),
          }}
        />
        {markers.map((m) => (
          <Tooltip key={m.uid} title={formatSpeakerLabel(m.speaker)}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                transform: "translateY(-50%)",
                left: pct(m.start, duration),
                width: pct(Math.max(0, m.end - m.start), duration),
                minWidth: 2,
                height: m.active ? 12 : 8,
                borderRadius: 1,
                backgroundColor: getSpeakerColorByLabel(
                  m.speaker,
                  speakers,
                  theme.palette.mode,
                ).primary,
                outline: m.active
                  ? `1px solid ${theme.palette.primary.main}`
                  : "none",
                opacity: m.active ? 1 : 0.65,
              }}
            />
          </Tooltip>
        ))}
        {armed?.start != null && armed?.end != null && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              transform: "translateY(-50%)",
              left: pct(Math.min(armed.start, armed.end), duration),
              width: pct(Math.abs(armed.end - armed.start), duration),
              height: 16,
              borderRadius: 1,
              border: `2px solid ${theme.palette.primary.main}`,
              backgroundColor: alpha(theme.palette.primary.main, 0.25),
            }}
          />
        )}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: pct(currentTime, duration),
            width: 2,
            backgroundColor: theme.palette.primary.main,
          }}
        />
      </Box>

      {!showPanel && (
        <Slider
          size="small"
          value={Math.min(currentTime, duration || 0)}
          min={0}
          max={duration || 100}
          step={0.01}
          onChange={(_, v) => onScrub(Array.isArray(v) ? v[0] : v)}
          aria-label="Seek"
          sx={{ py: 0.5 }}
        />
      )}

      {showPanel && (
        <Box
          sx={{
            mt: 1,
            p: 1.25,
            borderRadius: 2,
            border: `1px solid ${theme.palette.primary.main}`,
            backgroundColor: alpha(theme.palette.primary.main, 0.06),
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={1}
          >
            <Typography variant="caption" fontWeight={600} color="primary.main">
              {armedLabel ?? "Set time range"}
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontFamily: "monospace", color: "text.secondary" }}
            >
              {armedStart != null && armedEnd != null
                ? `${Math.abs(armedEnd - armedStart).toFixed(2)}s`
                : "—"}
            </Typography>
          </Box>

          {/* Type exact times, or snap to the playhead */}
          <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
            <TimestampField
              label="Start"
              value={armedStart}
              duration={duration}
              onCommit={(t) => handleSetStart(t)}
            />
            <Tooltip title="Set start to playhead">
              <IconButton size="small" onClick={() => handleSetStart()}>
                <MyLocationIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Box sx={{ color: "text.disabled" }}>→</Box>
            <TimestampField
              label="End"
              value={armedEnd}
              duration={duration}
              onCommit={(t) => handleSetEnd(t)}
            />
            <Tooltip title="Set end to playhead">
              <IconButton size="small" onClick={() => handleSetEnd()}>
                <MyLocationIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Fine seek: a zoomed window around the playhead for precise scrubbing */}
          <Box display="flex" alignItems="center" gap={1}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ whiteSpace: "nowrap" }}
            >
              Fine seek
            </Typography>
            <Slider
              size="small"
              value={clamp(currentTime, duration)}
              min={fineMin}
              max={fineMax}
              step={0.02}
              onChange={(_, v) => onScrub(Array.isArray(v) ? v[0] : v)}
              aria-label="Fine seek"
              sx={{ flexGrow: 1 }}
            />
            <Typography
              variant="caption"
              sx={{ fontFamily: "monospace", color: "text.secondary", minWidth: 64 }}
            >
              {formatPreciseTimestamp(currentTime)}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
            <Box flexGrow={1} />
            <Tooltip title="Back 0.5s">
              <IconButton size="small" onClick={() => step(-FRAME_STEP_LARGE)}>
                <Typography variant="caption">-0.5s</Typography>
              </IconButton>
            </Tooltip>
            <Tooltip title="Back one frame">
              <IconButton size="small" onClick={() => step(-FRAME_STEP_SMALL)}>
                <Typography variant="caption">-1f</Typography>
              </IconButton>
            </Tooltip>
            <Tooltip title="Forward one frame">
              <IconButton size="small" onClick={() => step(FRAME_STEP_SMALL)}>
                <Typography variant="caption">+1f</Typography>
              </IconButton>
            </Tooltip>
            <Tooltip title="Forward 0.5s">
              <IconButton size="small" onClick={() => step(FRAME_STEP_LARGE)}>
                <Typography variant="caption">+0.5s</Typography>
              </IconButton>
            </Tooltip>
          </Box>

          <Box display="flex" gap={1} mt={1}>
            <Button
              size="small"
              variant="contained"
              startIcon={<CheckIcon fontSize="small" />}
              onClick={handleCommit}
              disabled={!commitEnabled}
              fullWidth
              sx={{ textTransform: "none" }}
            >
              Done
            </Button>
            <Button
              size="small"
              variant="text"
              startIcon={<CloseIcon fontSize="small" />}
              onClick={handleCancel}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};
